import {
  ChangeDetectionStrategy,
  Component,
  effect,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';

import {Actions} from '@ngneat/effects-ng';
import {TreeNode} from '../../../models/rme/tree-node';

import {
  AttachmentObject,
  constants,
  NodePath,
  OrgInfo,
  REF_MODEL_STORE,
  Refmodel,
  RefModelStoreService,
  TakeUntilComponentDestroy
} from '@q9elements/ui-kit/common';
import {GridQuery, GridStore, loadEntities, removeEntity} from '@q9elements/ui-kit/components';

import {map as _map, get, isEqual, set} from 'lodash';
import {BehaviorSubject, combineLatest, EMPTY, Subscription} from 'rxjs';
import {catchError, filter, finalize, map, pairwise, tap} from 'rxjs/operators';

import {
  MAT_PAGINATOR_DEFAULT_OPTIONS,
  MatPaginatorDefaultOptions
} from '@angular/material/paginator';
import {RefModelSidebarService} from '@q9elements/ui-kit/refmodels';
import {IGridColumn} from 'src/app/custom-grid/models/grid-column.interface';
import {GridSelectorService} from 'src/app/custom-grid/services/grid-selector.service';
import {ITreeNode} from 'src/app/editor-interface/models/grid-node.interface';
import {TreeUpdateObject} from '../../../models/rme/tree-update-object';
import {ContextMenuActionsService} from '../../../new-context-menu/services/context-menu-actions.service';
import {AttachmentsService} from '../../../services/dao/attachments.service';
import {UpdateTreeService} from '../../../services/update-tree.service';
import {getObjectWithMaxTags} from '../../../util/grid-context-menu.utils';
import {ColumnsGenerateService} from '../../services/columns-generate.service';
import {GridUpdateService} from '../../services/grid-update.service';
import {NodeRemoveEmitterService} from '../../services/node-remove-emitter.service';
import {MyMetadataTagsColumn, OPTIMIZE_STATUS_PATH, TAGS_PATH} from './enums/my-metadata.enum';
import {MyMetadataQuery} from './state/my-metadata.query';
import {MyMetadataStore} from './state/my-metadata.store';

const SF_TYPE_FILTER_ALL = '';
const BULK_UPDATABLE_COLUMNS_PATH = {
  optimizeStatus: OPTIMIZE_STATUS_PATH,
  tags: TAGS_PATH
};

@Component({
  selector: 'app-my-metadata-view',
  templateUrl: './my-metadata-view.component.html',
  styleUrls: ['./my-metadata-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {provide: GridStore, useClass: MyMetadataStore},
    {provide: GridQuery, useClass: MyMetadataQuery},
    {
      provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
      useValue: {
        pageSize: constants.pagination.nodesPerPageDefault,
        pageSizeOptions: constants.pagination.pageSizeOptions
      } as MatPaginatorDefaultOptions
    }
  ]
})
export class MyMetadataViewComponent
  extends TakeUntilComponentDestroy
  implements OnInit, OnDestroy
{
  @Input()
  public set sfTypeFilter(value: string) {
    this.gridStore.update({type: value});
    this.actions.dispatch(loadEntities({reset: true}));
  }

  @Output() public selectRow = new EventEmitter<TreeNode>();

  private rowSelectedSubscription: Subscription;
  private activeItemsSubscription: Subscription;
  private injector: Injector = inject(Injector);
  private orgInfo$ = this.rmeStore.select<OrgInfo>(REF_MODEL_STORE.ORG_INFO);
  private refModel$ = this.rmeStore
    .select<Refmodel>(REF_MODEL_STORE.REF_MODEL)
    .pipe(tap(refModel => (this.isExternalRefModel = refModel.external)));
  private gdprStatuses: any;
  private updateTagsColumnWidth$ = new BehaviorSubject<number>(this.calculateTagsColumnWidth([]));

  public nodePath$ = this.rmeStore.select<NodePath[]>(REF_MODEL_STORE.NODE_PATH);
  public selectedNode: TreeNode;
  public isExternalRefModel: boolean;
  public columns$ = new BehaviorSubject<IGridColumn<ITreeNode>[]>([]);
  public idField = 'id';

  protected actions: Actions = inject(Actions);

  constructor(
    private rmeStore: RefModelStoreService,
    private gridSelector: GridSelectorService,
    private columnsGenerateService: ColumnsGenerateService,
    private attachmentsService: AttachmentsService,
    private contextMenuActionsService: ContextMenuActionsService,
    private nodeRemoveEmitterService: NodeRemoveEmitterService,
    private updateTreeService: UpdateTreeService,
    private gridUpdateService: GridUpdateService,
    private gridQuery: GridQuery,
    private gridStore: GridStore,
    private refModelSidebarService: RefModelSidebarService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.initSelectedNodeSubscription();
    this.initGDPRStatuses();
    this.initNodeRemoveEmitterSubscription();
    this.initUpdateNodeInTreeSubscription();
    this.initNodesBulkUpdateSubscription();
    this.initSelectedNodeAttachmentsStoreSubscription();
    this.actions.dispatch(loadEntities());
    this.updateInitialWidth();
    this.initializeColumns();
    this.onRowSelected();
    this.setUpEffect();
  }

  setUpEffect() {
    effect(
      () => {
        if (this.refModelSidebarService.reloadNodeTreeData()) {
          this.refModelSidebarService.reloadNodeTreeData.set(false);
          this.gridStore.update(state => ({
            ...state,
            reselectActiveEntity: true
          }));
          this.actions.dispatch(loadEntities({reset: true}));
        }
      },
      {allowSignalWrites: true, injector: this.injector}
    );
  }

  private initializeColumns(): void {
    combineLatest([this.orgInfo$, this.refModel$, this.updateTagsColumnWidth$])
      .pipe(
        this.takeUntilComponentDestroy(),
        map(([orgInfo, refModel, tagsColumnWidth]) => {
          const generatedColumns = this.columnsGenerateService.generateMyMetadataColumns(
            orgInfo,
            refModel,
            tagsColumnWidth
          );

          return generatedColumns.map(column => {
            if (column.field === MyMetadataTagsColumn.field) {
              return {...column, width: tagsColumnWidth};
            }
            return column;
          });
        }),
        catchError(() => {
          return EMPTY;
        }),
        finalize(() => {
          this.updateTagsWidth([]);
        })
      )
      .subscribe(updatedColumns => {
        this.columns$.next(updatedColumns);
      });
  }

  public onRowSelected(): void {
    this.activeItemsSubscription = this.gridQuery.selectActive().subscribe(nodes => {
      if (Array.isArray(nodes)) {
        this.handleRowSelect(nodes as TreeNode[]);
      }
    });
  }

  public handleRowSelect(selectedItems: TreeNode[]): void {
    this.gridSelector.handleKeySelectionChange(selectedItems);
    if (selectedItems.length) {
      this.selectedNode = selectedItems[0];
      this.selectRow.emit(this.selectedNode);
    }
  }

  private initGDPRStatuses(): void {
    this.gdprStatuses = {};
    _map(constants.gdprOptions.gdprStatuses, item => {
      this.gdprStatuses[item.key] = item.value;
    });
  }

  private initNodeRemoveEmitterSubscription() {
    this.nodeRemoveEmitterService
      .subscribeToEmitter()
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(nodeDate => this.actions.dispatch(removeEntity({id: nodeDate.nodeId})));
  }

  private initSelectedNodeSubscription(): void {
    this.rmeStore
      .select(REF_MODEL_STORE.SELECTED_NODE)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe((selectedNode): void => {
        if (selectedNode) {
          this.selectedNode = selectedNode as TreeNode;
        }
      });
  }

  private initSelectedNodeAttachmentsStoreSubscription(): void {
    this.rmeStore
      .select(REF_MODEL_STORE.SELECTED_NODE_ATTACHMENTS)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe((attachmentsObj: AttachmentObject) => this.updateAttachments(attachmentsObj));
  }

  private initUpdateNodeInTreeSubscription(): void {
    this.updateTreeService
      .select('updateNodeInTree')
      .pipe(this.takeUntilComponentDestroy(), filter(Boolean))
      .subscribe((data: TreeUpdateObject) => {
        if (data.property === 'tags') {
          this.updateTagsWidth(data.value);
        }

        this.gridQuery.store.update(data.nodeId, (entity: any) => {
          return {
            ...entity,
            ...set({}, data.property, data.value)
          };
        });
      });
  }

  private initNodesBulkUpdateSubscription() {
    this.contextMenuActionsService.nodesBulkUpdatedEmitter$
      .pipe(this.takeUntilComponentDestroy(), filter(Boolean))
      .subscribe((nodes: any[]) => {
        this.updateTagsWidth(nodes);

        nodes.forEach(node =>
          this.gridQuery.store.update(node.id, (entity: any) => {
            return {
              ...entity,
              ...this.getBulkUpdatedColumns(node)
            };
          })
        );
      });
  }

  private updateInitialWidth(): void {
    this.gridQuery
      .selectAll()
      .pipe(
        this.takeUntilComponentDestroy(),
        pairwise(),
        filter(([prev, curr]) => !isEqual(prev, curr))
      )
      .subscribe(([_, updatedNodes]) => {
        if (updatedNodes) {
          const objWithMaxTags = getObjectWithMaxTags(updatedNodes);
          this.updateTagsWidth(objWithMaxTags?.['tags']);
        }
      });
  }

  private calculateTagsColumnWidth(tags: string[]): number {
    if (typeof tags === 'string') {
      tags = String(tags).split(' ');
    }

    return this.gridUpdateService.calculateTagsColumnWidth([{tags}]);
  }

  private updateTagsWidth(tags: string[] = []): void {
    const newWidth = this.calculateTagsColumnWidth(tags);
    this.updateTagsColumnWidth$.next(newWidth);
  }

  private getBulkUpdatedColumns(node: any) {
    return Object.keys(BULK_UPDATABLE_COLUMNS_PATH).reduce((transformed, field) => {
      const path = BULK_UPDATABLE_COLUMNS_PATH[field];
      const value = get(node, path);

      return {
        ...transformed,
        [field]: value
      };
    }, {});
  }

  private updateAttachments(attachmentsObj: AttachmentObject) {
    if (!attachmentsObj?.loading) {
      const nodeId = this.selectedNode.id;
      const links = this.attachmentsService.getLinksNumber(attachmentsObj);

      this.gridQuery.store.update(nodeId, (entity: any) => {
        return {
          ...entity,
          links
        };
      });
    }
  }

  ngOnDestroy(): void {
    if (this.rowSelectedSubscription) {
      this.rowSelectedSubscription.unsubscribe();
    }
    if (this.activeItemsSubscription) {
      this.activeItemsSubscription.unsubscribe();
    }
  }
}
